import {AiAssetType, AssetSource} from 'common/src/modules/Assets/Assets.enums';
import {
  BlockingWidgetDisplayMode,
  CopilotWidgetType,
} from 'common/src/modules/shared/copilot/copilot.enums';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {AssetDisplayAndSelectWidgetContent} from '../../../widgets/AssetDisplayAndSelectWidget.types';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

// Schema for the tool input
const inputSchema = z.object({
  assetType: z
    .nativeEnum(AiAssetType)
    .describe('Type of assets to show/select. Only Image and Video are supported'),
  source: z
    .nativeEnum(AssetSource)
    .describe('Source of assets (TemplateAssets or BrandAssets)'),
  assetIds: z.array(z.number()).min(1).describe('Array of numeric asset IDs to show for selection'),
  maxSelectable: z
    .number()
    .min(0)
    .describe('Maximum number of assets user can select (0 = show only, no selection)'),
  message: z.string().describe('Message to show to user in the widget'),
  allowSelection: z.boolean().describe('Whether to allow user selection or just show assets'),
});

function letUserSeeOrSelectAssetTool() {
  return tool({
    name: AdBuilderAgentToolName.LetUserSeeOrSelectAsset,
    displayName: 'Show assets to the user with optional user selection',
    description:
      'Shows specific assets to the user for reference or allows them to select from the provided asset IDs. Only Image and Video asset types are supported.',
    inputSchema,
    execute: async ({
      input,
      toolUtils: {requestUserFeedback, showMessage},
    }: ToolExecuteParams<typeof inputSchema>) => {
      try {
        // Validate that only Image or Video types are requested
        if (input.assetType !== AiAssetType.Image && input.assetType !== AiAssetType.Video) {
          return toolResult(
            'ERROR: Only Image and Video asset types are supported for display/selection.',
          );
        }

        // Validate that we have asset IDs to work with
        if (input.assetIds.length === 0) {
          return toolResult('ERROR: No asset IDs provided. Please provide at least one asset ID.');
        }

        // Create widget content
        const widgetContent: AssetDisplayAndSelectWidgetContent = {
          assetType: input.assetType,
          source: input.source,
          assetIds: input.assetIds,
          maxSelectable: input.maxSelectable,
          message: input.message,
          allowSelection: input.allowSelection,
        };

        if (input.allowSelection && input.maxSelectable > 0) {
          // Selection mode - show blocking widget and wait for user selection
          const result = await requestUserFeedback({
            displayMode: BlockingWidgetDisplayMode.Inline,
            widget: {
              type: CopilotWidgetType.AssetDisplayAndSelectWidget,
              content: widgetContent,
            },
          });

          return toolResult(result); // results should be the user selected asset ids, or just a text saying 'assets has been displayed to the user'
        } else {
          // Display mode - just show assets for reference (non-blocking)
          showMessage({
            type: CopilotWidgetType.AssetDisplayAndSelectWidget,
            content: widgetContent,
          });

          return toolResult('Assets displayed for reference');
        }
      } catch (error) {
        return toolResult(
          `ERROR: ${error instanceof Error ? error.message : 'Failed to show/select assets'}`,
        );
      }
    },
  });
}

export default letUserSeeOrSelectAssetTool;
